import { Badge, type BadgeProps } from '@proerp/ui';
import type { ProjectAmpel, ProjectStatus, ProjectType } from '@proerp/shared';

/** Projektart → deutsches Label (ADR 0004). */
const PROJECT_TYPE_LABELS: Record<ProjectType, string> = {
  NEW_PLANT: 'Neuanlage',
  CONVERSION: 'Umbau',
  MAINTENANCE: 'Wartung',
  SERVICE: 'Service',
  OTHER: 'Sonstiges',
};

export const PROJECT_TYPE_OPTIONS = (Object.keys(PROJECT_TYPE_LABELS) as ProjectType[]).map(
  (value) => ({ value, label: PROJECT_TYPE_LABELS[value] }),
);

export function projectTypeLabel(type: ProjectType): string {
  return PROJECT_TYPE_LABELS[type];
}

/** Status → Badge-Ton + deutsches Label (Prozess-Lebenszyklus). Farbe nie ohne Text. */
const STATUS_MAP: Record<ProjectStatus, { tone: BadgeProps['tone']; label: string }> = {
  ANGELEGT: { tone: 'neutral', label: 'Angelegt' },
  ENGINEERING: { tone: 'info', label: 'Engineering' },
  BESCHAFFUNG: { tone: 'info', label: 'Beschaffung' },
  FERTIGUNG: { tone: 'info', label: 'Fertigung' },
  MONTAGE: { tone: 'accent', label: 'Montage' },
  IBN: { tone: 'accent', label: 'Inbetriebnahme' },
  ABGENOMMEN: { tone: 'success', label: 'Abgenommen' },
  GESCHLOSSEN: { tone: 'neutral', label: 'Geschlossen' },
  ON_HOLD: { tone: 'warning', label: 'Pausiert' },
  STORNIERT: { tone: 'danger', label: 'Storniert' },
};

export const PROJECT_STATUS_OPTIONS = (Object.keys(STATUS_MAP) as ProjectStatus[]).map((value) => ({
  value,
  label: STATUS_MAP[value].label,
}));

export function projectStatusLabel(status: ProjectStatus): string {
  return STATUS_MAP[status].label;
}

export function ProjectStatusBadge({ status }: { status: ProjectStatus }) {
  const { tone, label } = STATUS_MAP[status];
  return <Badge tone={tone}>{label}</Badge>;
}

/** Automatische Termin-Ampel (Gesundheit), getrennt vom Status. */
const AMPEL_MAP: Record<ProjectAmpel, { tone: BadgeProps['tone']; label: string; hint: string }> = {
  GRUEN: { tone: 'success', label: 'Grün', hint: 'Liefertermin nicht in Sicht gefährdet' },
  GELB: { tone: 'warning', label: 'Gelb', hint: 'Liefertermin in ≤ 14 Tagen oder Projekt pausiert' },
  ROT: { tone: 'danger', label: 'Rot', hint: 'Liefertermin überschritten' },
};

export const PROJECT_AMPEL_OPTIONS = (Object.keys(AMPEL_MAP) as ProjectAmpel[]).map((value) => ({
  value,
  label: AMPEL_MAP[value].label,
}));

export function ProjectAmpelBadge({ ampel }: { ampel: ProjectAmpel }) {
  const { tone, label, hint } = AMPEL_MAP[ampel];
  return (
    <span title={`Automatische Termin-Ampel: ${hint}`}>
      <Badge tone={tone}>● {label}</Badge>
    </span>
  );
}
